<template>
  <div class="chart-wrapper">
    <div
      class="chart-stage"
      :class="{
        'is-dragging': isDragging,
        'is-resizing-pane': isResizingPane,
        'is-hovering-pane-separator': isHoveringPaneSeparator,
        'is-hovering-right-axis': isHoveringRightAxis,
        'is-hovering-kline': hoveredIdx !== null,
      }"
    >
      <LeftToolbar
        ref="toolbarRef"
        :is-fullscreen="isFullscreen"
        @select-tool="handleSelectTool"
        @toggle-fullscreen="$emit('toggleFullscreen')"
        @zoom-in="applyZoomToLevel(zoomLevel + 1)"
        @zoom-out="applyZoomToLevel(zoomLevel - 1)"
        @settings-change="handleSettingsChange"
      />
      <div class="chart-main" ref="chartMainRef">
        <div class="pane-separator-layer" aria-hidden="true">
          <div
            v-for="line in paneSeparatorLines"
            :key="line.id"
            class="pane-separator-line"
            :class="{ 'is-active': hoveredPaneBoundaryId === line.id }"
            :style="{ top: `${line.top}px` }"
          ></div>
        </div>
        <div ref="tooltipLayerRef" class="tooltip-layer"></div>
        <div
          class="chart-container"
          ref="containerRef"
          @scroll.passive="onScroll"
          @pointerdown="onPointerDown"
          @pointermove="onPointerMove"
          @pointerup="onPointerUp"
          @pointerleave="onPointerLeave"
        >
          <!-- scroll-content 负责撑开横向滚动宽度，并承载 sticky 的画布层 -->
          <div class="scroll-content" :style="{ width: totalWidth + 'px' }">
            <!-- 画布层：sticky 固定在可视区域左上角，滚动只影响绘制时的 scrollLeft -->
            <div class="canvas-layer" ref="canvasLayerRef">
              <!-- plotCanvas 由 Chart 自动创建 -->

              <!-- 底部时间轴（随 X 滚动，但画布不移动） -->
              <canvas class="x-axis-canvas" ref="xAxisCanvasRef"></canvas>

              <DrawingStyleToolbar
                v-if="selectedDrawing"
                :drawing="selectedDrawing"
                @update-style="onUpdateDrawingStyle"
                @delete="onDeleteDrawing"
              />
            </div>
          </div>
        </div>
        <Teleport v-if="tooltipLayerRef" :to="tooltipLayerRef">
          <div
            v-if="hovered"
            class="tooltip-anchor kline-tooltip-anchor"
            :class="{ 'use-anchor': useAnchorPositioning }"
            :style="klineTooltipAnchorStyle"
          ></div>
          <div
            v-if="hoveredMarker || hoveredCustomMarker"
            class="tooltip-anchor marker-tooltip-anchor"
            :class="{ 'use-anchor': useAnchorPositioning }"
            :style="markerTooltipAnchorStyle"
          ></div>
          <KLineTooltip
            v-if="hovered"
            :k="hovered"
            :index="hoveredIndex"
            :data="chartData"
            :pos="teleportedTooltipPos"
            :set-el="setTooltipEl"
            :use-anchor="useAnchorPositioning"
            :anchor-placement="tooltipAnchorPlacement"
          />
          <MarkerTooltip
            v-if="hoveredMarker || hoveredCustomMarker"
            :marker="hoveredMarker || hoveredCustomMarker"
            :pos="teleportedMarkerTooltipPos"
            :use-anchor="useAnchorPositioning"
            :anchor-placement="markerTooltipAnchorPlacement"
            :set-el="setMarkerTooltipEl"
          />
        </Teleport>
        <div
          class="right-axis-host"
          ref="rightAxisLayerRef"
          :style="{ width: axisHostWidth + 'px' }"
          @pointerdown="onRightAxisPointerDown"
          @pointermove="onRightAxisPointerMove"
          @pointerup="onRightAxisPointerUp"
          @pointerleave="onRightAxisPointerLeave"
        ></div>
      </div>
    </div>
    <IndicatorSelector
      :active-indicators="activeIndicators"
      :indicator-params="indicatorParams"
      @toggle="handleIndicatorToggle"
      @update-params="handleUpdateParams"
      @reorder-sub-indicators="handleReorderSubIndicators"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick, shallowRef } from 'vue'
import { SemanticChartController, type SemanticChartConfig } from '@/semantic'
import { createCustomMarkersRenderer } from '@/core/renderers/customMarkers'
import KLineTooltip from './KLineTooltip.vue'
import MarkerTooltip from './MarkerTooltip.vue'
import IndicatorSelector from './IndicatorSelector.vue'
import DrawingStyleToolbar from './DrawingStyleToolbar.vue'
import { Chart, type PaneSpec } from '@/core/chart'
import { createChartStore, TRAILING_DRAWING_SLOTS, type ChartStore } from '@/core/chart-store'
import { zoomLevelToKWidth, kGapFromDpr, computeZoom, computeZoomToLevel } from '@/core/utils/zoom'
import { getPhysicalKLineConfig } from '@/core/utils/klineConfig'
import { createCandleRenderer } from '@/core/renderers/candle'
import { createGridLinesRendererPlugin } from '@/core/renderers/gridLines'
import { createLastPriceLineRendererPlugin } from '@/core/renderers/lastPrice'
import {
  createMARendererPlugin,
  createBOLLRendererPlugin,
  createEXPMARendererPlugin,
  createENERendererPlugin,
  createMainIndicatorLegendRendererPlugin,
  type SubIndicatorType,
  getMACDTitleInfo,
  getRSITitleInfo,
  getCCITitleInfo,
  getSTOCHTitleInfo,
  getMOMTitleInfo,
  getWMSRTitleInfo,
  getKSTTitleInfo,
  getFASTKTitleInfo,
} from '@/core/renderers/Indicator'
import { createExtremaMarkersRendererPlugin } from '@/core/renderers/extremaMarkers'
import { createYAxisRendererPlugin } from '@/core/renderers/yAxis'
import { createMacdScaleRendererPlugin } from '@/core/renderers/Indicator/scale/macd_scale'
import { createVolumeScaleRendererPlugin } from '@/core/renderers/Indicator/scale/volume_scale'
import { createRsiScaleRendererPlugin } from '@/core/renderers/Indicator/scale/rsi_scale'
import { createCciScaleRendererPlugin } from '@/core/renderers/Indicator/scale/cci_scale'
import { createStochScaleRendererPlugin } from '@/core/renderers/Indicator/scale/stoch_scale'
import { createMomScaleRendererPlugin } from '@/core/renderers/Indicator/scale/mom_scale'
import { createWmsrScaleRendererPlugin } from '@/core/renderers/Indicator/scale/wmsr_scale'
import { createKstScaleRendererPlugin } from '@/core/renderers/Indicator/scale/kst_scale'
import { createFastkScaleRendererPlugin } from '@/core/renderers/Indicator/scale/fastk_scale'
import { createTimeAxisRendererPlugin } from '@/core/renderers/timeAxis'
import { createCrosshairRendererPlugin } from '@/core/renderers/crosshair'
import { createPaneTitleRendererPlugin, type TitleInfo } from '@/core/renderers/paneTitle'
import type { InteractionSnapshot } from '@/core/controller/interaction'
import type { DrawingObject, DrawingStyle } from '@/plugin'
import LeftToolbar from './LeftToolbar.vue'
import { DrawingInteractionController, type DrawingToolId } from '@/core/drawing'

const props = withDefaults(
  defineProps<{
    /** 语义化配置（必需，唯一控制源） */
    semanticConfig: SemanticChartConfig

    yPaddingPx?: number
    minKWidth?: number
    maxKWidth?: number
    /** 右侧价格轴宽度 */
    rightAxisWidth?: number
    /** 底部时间轴高度 */
    bottomAxisHeight?: number
    /** 价格标签额外宽度（用于显示涨跌幅，默认 60px） */
    priceLabelWidth?: number

    /** 缩放级别数量（默认 10） */
    zoomLevels?: number
    /** 初始缩放级别（1 ~ zoomLevels，默认居中） */
    initialZoomLevel?: number
    /** 是否全屏 */
    isFullscreen?: boolean
  }>(),
  {
    yPaddingPx: 0,
    minKWidth: 2,
    maxKWidth: 50,
    rightAxisWidth: 0,
    bottomAxisHeight: 24,
    priceLabelWidth: 60,
    zoomLevels: 20,
    initialZoomLevel: 3,
    isFullscreen: false,
  },
)

const emit = defineEmits<{
  (e: 'zoomLevelChange', level: number, kWidth: number): void
  (e: 'toggleFullscreen'): void
}>()

const xAxisCanvasRef = ref<HTMLCanvasElement | null>(null)
const canvasLayerRef = ref<HTMLDivElement | null>(null)
const rightAxisLayerRef = ref<HTMLDivElement | null>(null)
const containerRef = ref<HTMLDivElement | null>(null)
const chartMainRef = ref<HTMLDivElement | null>(null)
const tooltipLayerRef = ref<HTMLDivElement | null>(null)
const toolbarRef = ref<InstanceType<typeof LeftToolbar> | null>(null)

/* ========== 十字线（鼠标悬停位置） ========== */
const chartRef = shallowRef<Chart | null>(null)

/* ========== 语义化控制器 ========== */
const semanticController = shallowRef<SemanticChartController | null>(null)

/* ========== ChartStore（响应式状态中心） ========== */
const store = createChartStore({
  initialZoomLevel: props.initialZoomLevel ?? 1,
  minKWidth: props.minKWidth,
  maxKWidth: props.maxKWidth,
  zoomLevels: props.zoomLevels,
  rightAxisWidth: props.rightAxisWidth,
  priceLabelWidth: props.priceLabelWidth,
})

// 初始化 kWidth / kGap
store.actions.setZoomState(
  store.state.zoomLevel,
  zoomLevelToKWidth(store.state.zoomLevel, {
    minKWidth: props.minKWidth,
    maxKWidth: props.maxKWidth,
    zoomLevelCount: props.zoomLevels,
    dpr: store.state.viewportDpr,
  }),
  kGapFromDpr(store.state.viewportDpr),
)

// 为逐步迁移保留的局部别名
const dataLength = computed(() => store.state.dataLength)
const viewportDpr = computed(() => store.state.viewportDpr)
const zoomLevel = computed(() => store.state.zoomLevel)
const kWidth = computed(() => store.state.kWidth)
const kGap = computed(() => store.state.kGap)
const paneRatios = computed(() => store.state.paneRatios)
const selectedDrawingId = computed(() => store.state.selectedDrawingId)
const dataVersion = computed(() => store.state.dataVersion)

function scheduleRender() {
  chartRef.value?.scheduleDraw()
}

function handleSettingsChange(settings: Record<string, boolean>) {
  chartRef.value?.updateSettings(settings)
}

function measureTooltipSize(el: HTMLDivElement, minWidth: number, minHeight: number) {
  const r = el.getBoundingClientRect()
  return {
    width: Math.max(minWidth, Math.round(r.width)),
    height: Math.max(minHeight, Math.round(r.height)),
  }
}

function setTooltipEl(el: HTMLDivElement | null) {
  if (!el) return
  const size = measureTooltipSize(el, 180, 80)
  chartRef.value?.interaction.setTooltipSize(size)
}

function setMarkerTooltipEl(el: HTMLDivElement | null) {
  if (!el) return
  markerTooltipSize.value = measureTooltipSize(el, 120, 60)
}

// ===== Marker tooltip 状态 =====
const mousePos = ref({ x: 0, y: 0 })
const useAnchorPositioning = ref(false)

// ===== 交互状态（单一来源：InteractionController snapshot） =====
const interactionState = shallowRef<InteractionSnapshot>({
  crosshairPos: null,
  crosshairIndex: null,
  crosshairPrice: null,
  hoveredIndex: null,
  activePaneId: null,
  tooltipPos: { x: 0, y: 0 },
  tooltipAnchorPlacement: 'right-bottom',
  hoveredMarkerData: null,
  hoveredCustomMarker: null,
  isDragging: false,
  isResizingPaneBoundary: false,
  isHoveringPaneBoundary: false,
  hoveredPaneBoundaryId: null,
  isHoveringRightAxis: false,
})

const drawingController = shallowRef<DrawingInteractionController | null>(null)
const selectedDrawing = computed(() => {
  const id = selectedDrawingId.value
  if (!id) return null
  return store.state.drawings.find((d) => d.id === id) ?? null
})
const paneSeparatorLines = ref<Array<{ id: string; top: number }>>([])
const markerTooltipSize = ref({ width: 220, height: 120 })
const tooltipLayerOffset = computed(() => {
  const container = containerRef.value
  const chartMain = chartMainRef.value
  if (!container || !chartMain) return { x: 0, y: 0 }
  return {
    x: container.offsetLeft,
    y: container.offsetTop,
  }
})

const hoveredMarker = computed(() => interactionState.value.hoveredMarkerData)
const hoveredCustomMarker = computed(() => interactionState.value.hoveredCustomMarker)
const isDragging = computed(() => interactionState.value.isDragging)
const isResizingPane = computed(() => interactionState.value.isResizingPaneBoundary)
const isHoveringPaneSeparator = computed(() => interactionState.value.isHoveringPaneBoundary)
const hoveredPaneBoundaryId = computed(() => interactionState.value.hoveredPaneBoundaryId)
const isHoveringRightAxis = computed(() => interactionState.value.isHoveringRightAxis)
const hoveredIdx = computed(() => interactionState.value.hoveredIndex)
const crosshairIdx = computed(() => interactionState.value.crosshairIndex)

const hovered = computed(() => {
  const idx = interactionState.value.hoveredIndex
  if (typeof idx !== 'number') return null
  void dataVersion.value // 建立响应式依赖
  const data = chartRef.value?.getData()
  if (data && idx >= 0 && idx < data.length) {
    return data[idx]
  }
  return null
})
const hoveredIndex = computed(() => interactionState.value.hoveredIndex)
const tooltipPos = computed(() => interactionState.value.tooltipPos)
const teleportedTooltipPos = computed(() => ({
  x: tooltipPos.value.x + tooltipLayerOffset.value.x,
  y: tooltipPos.value.y + tooltipLayerOffset.value.y,
}))
const klineTooltipAnchorStyle = computed(() => ({
  left: `${teleportedTooltipPos.value.x}px`,
  top: `${teleportedTooltipPos.value.y}px`,
}))
const teleportedMarkerTooltipPos = computed(() => ({
  x: mousePos.value.x + tooltipLayerOffset.value.x,
  y: mousePos.value.y + tooltipLayerOffset.value.y,
}))
const markerTooltipAnchorStyle = computed(() => ({
  left: `${teleportedMarkerTooltipPos.value.x}px`,
  top: `${teleportedMarkerTooltipPos.value.y}px`,
}))
const tooltipAnchorPlacement = computed(() => interactionState.value.tooltipAnchorPlacement)
const markerTooltipAnchorPlacement = computed<'right-bottom' | 'left-bottom'>(() => {
  const chart = chartRef.value
  const viewport = chart?.getViewport()
  const container = containerRef.value
  const plotWidth = viewport?.plotWidth ?? (container ? container.clientWidth : 0)
  const padding = 12
  const gap = 12
  const rightCandidateX = mousePos.value.x + gap
  const wouldOverflowRight = rightCandidateX + markerTooltipSize.value.width + padding > plotWidth
  return wouldOverflowRight ? 'left-bottom' : 'right-bottom'
})

// 获取当前图表数据
const chartData = computed(() => {
  void dataVersion.value // 建立响应式依赖，确保数据变化时重新求值
  return chartRef.value?.getData() ?? []
})

// 通知数据变化（在数据更新后调用）
function handleSelectTool(toolId: string) {
  drawingController.value?.setTool(toolId as DrawingToolId)
}

function onUpdateDrawingStyle(style: Partial<DrawingStyle>) {
  const d = selectedDrawing.value
  if (!d || !drawingController.value) return
  drawingController.value.updateDrawingStyle(d.id, style)
  store.actions.bumpDrawingVersion()
}

function onDeleteDrawing() {
  const d = selectedDrawing.value
  if (!d || !drawingController.value) return
  drawingController.value.removeDrawing(d.id)
  store.actions.setSelectedDrawingId(null)
  store.actions.bumpDrawingVersion()
  store.actions.setDrawings(drawingController.value.getDrawings())
}

function onPointerDown(e: PointerEvent) {
  const container = containerRef.value
  if (!container) return

  // 优先处理绘图交互
  if (drawingController.value?.onPointerDown(e, container)) {
    store.actions.setDrawings(drawingController.value.getDrawings())
    store.actions.bumpDrawingVersion()
    return
  }

  chartRef.value?.interaction.onPointerDown(e)
}

function onPointerMove(e: PointerEvent) {
  const container = containerRef.value
  if (container) {
    const rect = container.getBoundingClientRect()
    mousePos.value = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    }
    if (drawingController.value?.onPointerMove(e, container)) {
      store.actions.setDrawings(drawingController.value.getDrawings())
      return
    }
  }
  chartRef.value?.interaction.onPointerMove(e)
}

function onPointerUp(e: PointerEvent) {
  const container = containerRef.value
  if (container && drawingController.value?.onPointerUp(e, container)) {
    store.actions.setDrawings(drawingController.value.getDrawings())
    return
  }
  chartRef.value?.interaction.onPointerUp(e)
}

function onPointerLeave(e: PointerEvent) {
  chartRef.value?.interaction.onPointerLeave(e)
}

function onRightAxisPointerDown(e: PointerEvent) {
  chartRef.value?.interaction.onRightAxisPointerDown(e)
}

function onRightAxisPointerMove(e: PointerEvent) {
  chartRef.value?.interaction.onRightAxisPointerMove(e)
}

function onRightAxisPointerUp(e: PointerEvent) {
  chartRef.value?.interaction.onRightAxisPointerUp(e)
}

function onRightAxisPointerLeave(e: PointerEvent) {
  chartRef.value?.interaction.onRightAxisPointerLeave(e)
}

function onScroll() {
  chartRef.value?.interaction.onScroll()
}

// 指标选择器状态（由 semanticConfig 初始化）
const activeIndicators = ref<string[]>([])

// 指标参数配置（MA 的 periods 是数组，需要更宽松的类型）
const indicatorParams = ref<Record<string, Record<string, unknown>>>({})

// 副图槽位状态
interface SubPaneSlot {
  id: string // pane ID: 'sub_0', 'sub_1', ...
  indicatorId: SubIndicatorType
  rendererName: string
  paneTitleRendererName: string // paneTitle 渲染器名称
  params: Record<string, unknown>
}

// 副图槽位数组（支持多副图）
const subPanes = ref<SubPaneSlot[]>([])

// 副图指标元数据
interface SubPaneIndicatorConfig {
  defaultParams: Record<string, number>
  getTitleInfo: (
    data: any[],
    index: number | null,
    params: Record<string, number>,
  ) => TitleInfo | null
}

const SUB_PANE_INDICATOR_CONFIGS: Record<SubIndicatorType, SubPaneIndicatorConfig> = {
  VOLUME: {
    defaultParams: {},
    getTitleInfo: () => ({ name: 'VOL', params: [], values: [] }),
  },
  MACD: {
    defaultParams: { fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 },
    getTitleInfo: (data, index, params) => {
      if (index === null) return null
      return getMACDTitleInfo(
        data,
        index,
        params.fastPeriod ?? 12,
        params.slowPeriod ?? 26,
        params.signalPeriod ?? 9,
      )
    },
  },
  RSI: {
    defaultParams: { period1: 6, period2: 12, period3: 24 },
    getTitleInfo: (data, index, params) => {
      if (index === null) return null
      return getRSITitleInfo(
        data,
        index,
        params.period1 ?? 6,
        params.period2 ?? 12,
        params.period3 ?? 24,
      )
    },
  },
  CCI: {
    defaultParams: { period: 14 },
    getTitleInfo: (data, index, params) => {
      if (index === null) return null
      return getCCITitleInfo(data, index, params.period ?? 14)
    },
  },
  STOCH: {
    defaultParams: { n: 9, m: 3 },
    getTitleInfo: (data, index, params) => {
      if (index === null) return null
      return getSTOCHTitleInfo(data, index, params.n ?? 9, params.m ?? 3)
    },
  },
  MOM: {
    defaultParams: { period: 10 },
    getTitleInfo: (data, index, params) => {
      if (index === null) return null
      return getMOMTitleInfo(data, index, params.period ?? 10)
    },
  },
  WMSR: {
    defaultParams: { period: 14 },
    getTitleInfo: (data, index, params) => {
      if (index === null) return null
      return getWMSRTitleInfo(data, index, params.period ?? 14)
    },
  },
  KST: {
    defaultParams: { roc1: 10, roc2: 15, roc3: 20, roc4: 30, signalPeriod: 9 },
    getTitleInfo: (data, index, params) => {
      if (index === null) return null
      return getKSTTitleInfo(
        data,
        index,
        params.roc1 ?? 10,
        params.roc2 ?? 15,
        params.roc3 ?? 20,
        params.roc4 ?? 30,
        params.signalPeriod ?? 9,
      )
    },
  },
  FASTK: {
    defaultParams: { period: 9 },
    getTitleInfo: (data, index, params) => {
      if (index === null) return null
      return getFASTKTitleInfo(data, index, params.period ?? 9)
    },
  },
}

// 副图指标列表
const SUB_PANE_INDICATORS = Object.keys(SUB_PANE_INDICATOR_CONFIGS) as SubIndicatorType[]

// 最大副图数量
const maxSubPanes = 4

function buildPaneLayoutIntent(): PaneSpec[] {
  const mainRatio = paneRatios.value['main'] ?? 3
  return subPanes.value.length === 0
    ? [{ id: 'main', ratio: mainRatio, visible: true, role: 'price' }]
    : [
        { id: 'main', ratio: mainRatio, visible: true, role: 'price' },
        ...subPanes.value.map((pane) => ({
          id: pane.id,
          ratio: paneRatios.value[pane.id] ?? 1,
          visible: true,
          role: 'indicator' as const,
        })),
      ]
}

// 获取指标默认参数
function getDefaultParams(indicatorId: SubIndicatorType): Record<string, number> {
  return { ...SUB_PANE_INDICATOR_CONFIGS[indicatorId].defaultParams }
}

// 添加副图（使用 Chart API）
function addSubPane(
  indicatorId: SubIndicatorType = 'VOLUME',
  params?: Record<string, number>,
): boolean {
  if (subPanes.value.length >= maxSubPanes) {
    return false
  }

  const paneId = `sub_${indicatorId}`

  // 已存在则跳过
  if (subPanes.value.some((p) => p.id === paneId)) {
    return true
  }

  // 使用 Chart API 创建副图（pane + 指标渲染器）
  const success = chartRef.value?.createSubPane(
    indicatorId,
    params ?? getDefaultParams(indicatorId),
  )
  if (!success) return false

  // 创建 paneTitle 渲染器（UI 层职责）
  const paneTitleRenderer = createPaneTitleRendererPlugin({
    paneId,
    title: indicatorId,
    getTitleInfo: () => getSubPaneTitleInfo(paneId),
  })
  chartRef.value?.useRenderer(paneTitleRenderer)

  // 更新本地状态
  subPanes.value.push({
    id: paneId,
    indicatorId,
    rendererName: `${indicatorId.toLowerCase()}_${paneId}`,
    paneTitleRendererName: paneTitleRenderer.name,
    params: params ?? getDefaultParams(indicatorId),
  })

  // 新增副图后，由 Chart 回流 ratio

  // 更新 activeIndicators
  if (!activeIndicators.value.includes(indicatorId)) {
    activeIndicators.value.push(indicatorId)
  }

  scheduleRender()

  return true
}

// 移除副图（使用 Chart API）
function removeSubPane(paneId: string): void {
  const index = subPanes.value.findIndex((p) => p.id === paneId)
  if (index === -1) return

  const pane = subPanes.value[index]
  if (!pane) return

  // 移除 paneTitle 渲染器
  chartRef.value?.removeRenderer(pane.paneTitleRendererName)

  // 使用 Chart API 移除副图（pane + 指标渲染器）
  chartRef.value?.removeSubPane(pane.indicatorId)

  // 更新本地状态
  subPanes.value.splice(index, 1)

  // 移除副图后，由 Chart 回流 ratio

  // 更新 activeIndicators
  const hasOtherPane = subPanes.value.some((p) => p.indicatorId === pane.indicatorId)
  if (!hasOtherPane) {
    activeIndicators.value = activeIndicators.value.filter((id) => id !== pane.indicatorId)
  }
}

// 清除所有副图（使用 Chart API）
function clearAllSubPanes(): void {
  // 移除所有 paneTitle 渲染器
  for (const pane of subPanes.value) {
    chartRef.value?.removeRenderer(pane.paneTitleRendererName)
  }

  // 使用 Chart API 清除所有副图
  chartRef.value?.clearSubPanes()

  // 清空本地状态
  subPanes.value = []
  activeIndicators.value = activeIndicators.value.filter(
    (id) => !SUB_PANE_INDICATORS.includes(id as SubIndicatorType),
  )
}

// 从语义化配置初始化指标状态（单向数据流：config → state）
function initIndicatorsFromConfig(): void {
  const config = props.semanticConfig

  // 初始化主图指标
  const mainIndicators = config.indicators?.main
  if (mainIndicators) {
    for (const indicator of mainIndicators) {
      if (indicator.enabled) {
        if (!activeIndicators.value.includes(indicator.type)) {
          activeIndicators.value.push(indicator.type)
        }
        // 初始化参数
        if (indicator.params) {
          indicatorParams.value[indicator.type] = indicator.params as Record<string, unknown>
        }
      }
    }
  }

  // 副图指标参数由 syncSubPanesFromChart 处理
}

// 监听主图指标状态和参数变化，控制渲染器（单向数据流：state → chart）
watch(
  [activeIndicators, indicatorParams],
  ([indicators]) => {
    const chart = chartRef.value
    if (!chart) return

    // 更新 mainIndicatorLegend 渲染器配置
    chart.updateRendererConfig('mainIndicatorLegend', {
      indicators: {
        MA: {
          enabled: indicators.includes('MA'),
          params: indicatorParams.value['MA'] || {},
        },
        BOLL: {
          enabled: indicators.includes('BOLL'),
          params: indicatorParams.value['BOLL'] || {},
        },
        EXPMA: {
          enabled: indicators.includes('EXPMA'),
          params: indicatorParams.value['EXPMA'] || {},
        },
        ENE: {
          enabled: indicators.includes('ENE'),
          params: indicatorParams.value['ENE'] || {},
        },
      },
    })

    // MA 线渲染器
    chart.setRendererEnabled('ma', indicators.includes('MA'))

    // BOLL 线渲染器
    chart.setRendererEnabled('boll', indicators.includes('BOLL'))

    // EXPMA 线渲染器
    chart.setRendererEnabled('expma', indicators.includes('EXPMA'))

    // ENE 线渲染器
    chart.setRendererEnabled('ene', indicators.includes('ENE'))

    scheduleRender()
  },
  { deep: true },
)

// 从 Chart 同步副图状态到本地（语义化配置后调用）
function syncSubPanesFromChart(): void {
  const chartSubPanes = chartRef.value?.getSubPaneIndicators() ?? []

  // 清空本地状态
  subPanes.value = []

  for (const indicatorId of chartSubPanes) {
    const paneId = `sub_${indicatorId}`

    // 创建 paneTitle 渲染器
    const paneTitleRenderer = createPaneTitleRendererPlugin({
      paneId,
      title: indicatorId,
      getTitleInfo: () => getSubPaneTitleInfo(paneId),
    })
    chartRef.value?.useRenderer(paneTitleRenderer)

    // 更新本地状态
    subPanes.value.push({
      id: paneId,
      indicatorId,
      rendererName: `${indicatorId.toLowerCase()}_${paneId}`,
      paneTitleRendererName: paneTitleRenderer.name,
      params: getDefaultParams(indicatorId),
    })

    // 更新 activeIndicators
    if (!activeIndicators.value.includes(indicatorId)) {
      activeIndicators.value.push(indicatorId)
    }
  }

  scheduleRender()
}

// 切换副图指标（使用 Chart API）
function switchSubIndicator(paneId: string, newIndicatorId: SubIndicatorType): void {
  const pane = subPanes.value.find((p) => p.id === paneId)
  if (!pane) return

  const oldIndicatorId = pane.indicatorId

  // 移除旧的 paneTitle 渲染器
  chartRef.value?.removeRenderer(pane.paneTitleRendererName)

  // 使用 Chart API 移除旧副图
  chartRef.value?.removeSubPane(oldIndicatorId)

  // 使用 Chart API 创建新副图
  chartRef.value?.createSubPane(newIndicatorId, getDefaultParams(newIndicatorId))

  // 创建新的 paneTitle 渲染器
  const newPaneId = `sub_${newIndicatorId}`
  const paneTitleRenderer = createPaneTitleRendererPlugin({
    paneId: newPaneId,
    title: newIndicatorId,
    getTitleInfo: () => getSubPaneTitleInfo(newPaneId),
  })
  chartRef.value?.useRenderer(paneTitleRenderer)

  // 更新本地状态
  const index = subPanes.value.findIndex((p) => p.id === paneId)
  if (index !== -1) {
    subPanes.value[index] = {
      id: newPaneId,
      indicatorId: newIndicatorId,
      rendererName: `${newIndicatorId.toLowerCase()}_${newPaneId}`,
      paneTitleRendererName: paneTitleRenderer.name,
      params: getDefaultParams(newIndicatorId),
    }
  }

  // 更新 activeIndicators：移除旧指标，添加新指标
  activeIndicators.value = activeIndicators.value.filter((id) => id !== oldIndicatorId)
  if (!activeIndicators.value.includes(newIndicatorId)) {
    activeIndicators.value.push(newIndicatorId)
  }
}

// 获取副图标题信息
function getSubPaneTitleInfo(paneId: string): TitleInfo | null {
  const pane = subPanes.value.find((p) => p.id === paneId)
  if (!pane) return null

  const data = chartRef.value?.getData()
  if (!data || data.length === 0) return null

  const config = SUB_PANE_INDICATOR_CONFIGS[pane.indicatorId]
  const params = pane.params as Record<string, number>
  return config.getTitleInfo(data, crosshairIdx.value, params)
}

// 指标切换处理（只更新状态，渲染器由 watch 控制）
function handleIndicatorToggle(indicatorId: string, active: boolean) {
  // 主图指标处理
  if (
    indicatorId === 'MA' ||
    indicatorId === 'BOLL' ||
    indicatorId === 'EXPMA' ||
    indicatorId === 'ENE'
  ) {
    if (active) {
      if (!activeIndicators.value.includes(indicatorId)) {
        activeIndicators.value.push(indicatorId)
      }
    } else {
      activeIndicators.value = activeIndicators.value.filter((id) => id !== indicatorId)
    }
    // 渲染器状态由 watch activeIndicators 控制
    return
  }

  // 副图指标处理
  if (SUB_PANE_INDICATORS.includes(indicatorId as SubIndicatorType)) {
    if (active) {
      // 更新 activeIndicators 状态
      if (!activeIndicators.value.includes(indicatorId)) {
        activeIndicators.value.push(indicatorId)
      }

      // 检查是否已有该指标的 pane
      const existingPane = subPanes.value.find((p) => p.indicatorId === indicatorId)
      if (existingPane) {
        // 已存在，无需再添加
        return
      }

      // 尝试添加新副图
      if (!addSubPane(indicatorId as SubIndicatorType)) {
        // 达到上限，替换最后一个
        const lastPane = subPanes.value[subPanes.value.length - 1]
        if (lastPane) {
          switchSubIndicator(lastPane.id, indicatorId as SubIndicatorType)
        }
      }
    } else {
      // 更新 activeIndicators 状态
      activeIndicators.value = activeIndicators.value.filter((id) => id !== indicatorId)

      // 找到并移除该指标的所有 pane
      const panesToRemove = subPanes.value.filter((p) => p.indicatorId === indicatorId)
      panesToRemove.forEach((pane) => removeSubPane(pane.id))
    }
    scheduleRender()
  }
}

// 更新主图指标图例配置
function updateMainIndicatorLegendConfig() {
  chartRef.value?.updateRendererConfig('mainIndicatorLegend', {
    indicators: {
      MA: {
        enabled: activeIndicators.value.includes('MA'),
        params: indicatorParams.value['MA'] || {},
      },
      BOLL: {
        enabled: activeIndicators.value.includes('BOLL'),
        params: indicatorParams.value['BOLL'] || {},
      },
      EXPMA: {
        enabled: activeIndicators.value.includes('EXPMA'),
        params: indicatorParams.value['EXPMA'] || {},
      },
      ENE: {
        enabled: activeIndicators.value.includes('ENE'),
        params: indicatorParams.value['ENE'] || {},
      },
    },
  })
}

// 指标参数更新处理
function handleUpdateParams(indicatorId: string, params: Record<string, unknown>) {
  // 保存参数配置
  indicatorParams.value[indicatorId] = params

  // 主图指标参数更新
  if (
    indicatorId === 'MA' ||
    indicatorId === 'BOLL' ||
    indicatorId === 'EXPMA' ||
    indicatorId === 'ENE'
  ) {
    // BOLL 渲染器配置
    if (indicatorId === 'BOLL') {
      chartRef.value?.updateRendererConfig('boll', params)
    }
    // EXPMA 渲染器配置
    if (indicatorId === 'EXPMA') {
      chartRef.value?.updateRendererConfig('expma', params)
    }
    // ENE 渲染器配置
    if (indicatorId === 'ENE') {
      chartRef.value?.updateRendererConfig('ene', params)
    }
    // 更新图例以显示新参数
    updateMainIndicatorLegendConfig()
    scheduleRender()
    return
  }

  // 副图指标参数更新
  if (SUB_PANE_INDICATORS.includes(indicatorId as SubIndicatorType)) {
    // 更新所有使用该指标的 pane
    subPanes.value
      .filter((p) => p.indicatorId === indicatorId)
      .forEach((pane) => {
        pane.params = { ...params }
        chartRef.value?.updateRendererConfig(pane.rendererName, params)
      })
  }

  scheduleRender()
}

function handleReorderSubIndicators(orderedIndicatorIds: string[]) {
  if (!orderedIndicatorIds.length || subPanes.value.length <= 1) return

  const validOrder = orderedIndicatorIds.filter((id): id is SubIndicatorType =>
    SUB_PANE_INDICATORS.includes(id as SubIndicatorType),
  )
  if (!validOrder.length) return

  const paneByIndicator = new Map(subPanes.value.map((pane) => [pane.indicatorId, pane] as const))
  const nextSubPanes: SubPaneSlot[] = []

  for (const indicatorId of validOrder) {
    const pane = paneByIndicator.get(indicatorId)
    if (pane) {
      nextSubPanes.push(pane)
      paneByIndicator.delete(indicatorId)
    }
  }

  if (nextSubPanes.length === 0) return

  for (const pane of subPanes.value) {
    if (paneByIndicator.has(pane.indicatorId)) {
      nextSubPanes.push(pane)
      paneByIndicator.delete(pane.indicatorId)
    }
  }

  const currentSubIds = subPanes.value.map((p) => p.id)
  const nextSubIds = nextSubPanes.map((p) => p.id)
  if (currentSubIds.join('|') === nextSubIds.join('|')) return

  subPanes.value = nextSubPanes

  const currentMainIndicators = activeIndicators.value.filter(
    (id) => !SUB_PANE_INDICATORS.includes(id as SubIndicatorType),
  )
  const subIndicatorOrder = subPanes.value.map((pane) => pane.indicatorId)
  activeIndicators.value = [...currentMainIndicators, ...subIndicatorOrder]

  const chart = chartRef.value
  if (!chart) return
  chart.updatePaneLayout(buildPaneLayoutIntent())
}

/* 计算总宽度：从 Vue 响应式状态读取，zoom 变化时自动重算 */
const axisHostWidth = computed(() => props.rightAxisWidth + props.priceLabelWidth)

const TRAILING_DRAWING_SLOTS_VAL = TRAILING_DRAWING_SLOTS

const totalWidth = store.computed.totalWidth

// 缩放由 Chart 回调驱动 scrollLeft 与渲染时序。

function scrollToRight() {
  const container = containerRef.value
  const chart = chartRef.value
  if (!container || !chart) return

  const dataLength = chart.getData()?.length ?? 0
  if (dataLength === 0) return

  const dpr = chart.getCurrentDpr()
  const { unitPx, startXPx } = getPhysicalKLineConfig(kWidth.value, kGap.value, dpr)

  // 计算最后一根K线的结束位置（不含 TRAILING_DRAWING_SLOTS）
  const lastKLineEndPx = (startXPx + dataLength * unitPx) / dpr

  // 计算最大可滚动距离
  const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth)

  // 计算需要的滚动位置，使最后一根K线紧贴最右侧
  const targetScrollLeft = Math.min(
    maxScrollLeft,
    Math.max(0, lastKLineEndPx - container.clientWidth)
  )

  container.scrollLeft = Math.round(targetScrollLeft * dpr) / dpr
  scheduleRender()
}

/* 缩放到指定级别（Vue 层驱动） */
function applyZoomToLevel(targetLevel: number, anchorX?: number) {
  const chart = chartRef.value
  const container = containerRef.value
  if (!chart || !container) return
  const dpr = chart.getCurrentDpr()
  const centerX = anchorX ?? (chart.getViewport()?.plotWidth ?? container.clientWidth) / 2
  const result = computeZoomToLevel(
    targetLevel,
    centerX,
    container.scrollLeft,
    zoomLevel.value,
    kWidth.value,
    kGap.value,
    {
      minKWidth: props.minKWidth,
      maxKWidth: props.maxKWidth,
      zoomLevelCount: props.zoomLevels,
      dpr,
    },
  )
  if (!result) return
  store.actions.setZoomState(result.targetLevel, result.newKWidth, result.newKGap)
  chart.interaction.clearHover()
  nextTick(() => {
    const c = containerRef.value
    if (!c) return
    const max = Math.max(0, c.scrollWidth - c.clientWidth)
    const clampedScrollLeft = Math.min(Math.max(0, result.newScrollLeft), max)
    c.scrollLeft = Math.round(clampedScrollLeft * dpr) / dpr
    chart.applyRenderState(result.newKWidth, result.newKGap, result.targetLevel)
    emit('zoomLevelChange', result.targetLevel, result.newKWidth)
  })
}

defineExpose({
  scheduleRender,
  scrollToRight,
  addSubPane,
  removeSubPane,
  switchSubIndicator,
  clearAllSubPanes,
  get plugin() {
    return chartRef.value?.plugin
  },

  // Zoom Level API（Vue SSOT）
  zoomToLevel: applyZoomToLevel,
  zoomIn: (anchorX?: number) => applyZoomToLevel(zoomLevel.value + 1, anchorX),
  zoomOut: (anchorX?: number) => applyZoomToLevel(zoomLevel.value - 1, anchorX),
  getZoomLevel: () => zoomLevel.value,
  getZoomLevelCount: () => chartRef.value?.getZoomLevelCount() ?? 10,
})

onMounted(() => {
  useAnchorPositioning.value =
    typeof CSS !== 'undefined' &&
    CSS.supports('anchor-name: --kmap-anchor') &&
    CSS.supports('position-anchor: --kmap-anchor')

  const container = containerRef.value
  const canvasLayer = canvasLayerRef.value
  const rightAxisLayer = rightAxisLayerRef.value
  const xAxisCanvas = xAxisCanvasRef.value
  if (!container || !canvasLayer || !rightAxisLayer || !xAxisCanvas) return

  // 手动添加 wheel 事件监听器，设置 passive: false 以允许 preventDefault()
  const onWheelHandler = (e: WheelEvent) => {
    e.preventDefault()
    const chart = chartRef.value
    if (!chart) return

    const rect = container.getBoundingClientRect()
    const mouseX = e.clientX - rect.left
    const scrollLeft = container.scrollLeft
    const dpr = chart.getCurrentDpr()

    const result = computeZoom(
      e.deltaY > 0 ? -1 : 1,
      mouseX,
      scrollLeft,
      zoomLevel.value,
      kWidth.value,
      kGap.value,
      {
        minKWidth: props.minKWidth,
        maxKWidth: props.maxKWidth,
        zoomLevelCount: props.zoomLevels,
        dpr,
      },
    )
    if (!result) return

    // 更新 Vue 响应式状态 → totalWidth 重算
    store.actions.setZoomState(result.targetLevel, result.newKWidth, result.newKGap)

    // 清除 hover + crosshair
    chart.interaction.clearHover()

    nextTick(() => {
      const c = containerRef.value
      if (!c) return
      const maxScrollLeft = Math.max(0, c.scrollWidth - c.clientWidth)
      const clampedScrollLeft = Math.min(Math.max(0, result.newScrollLeft), maxScrollLeft)
      c.scrollLeft = Math.round(clampedScrollLeft * dpr) / dpr
      chart.applyRenderState(result.newKWidth, result.newKGap, result.targetLevel)
      emit('zoomLevelChange', result.targetLevel, result.newKWidth)
    })
  }
  container.addEventListener('wheel', onWheelHandler, { passive: false })

  // 主图初始创建，副图由 addSubPane 动态添加
  const chart = new Chart(
    { container, canvasLayer, rightAxisLayer, xAxisCanvas },
    {
      // kWidth/kGap 由 zoomLevel 派生，不从 props 注入
      yPaddingPx: props.yPaddingPx,
      rightAxisWidth: props.rightAxisWidth,
      bottomAxisHeight: props.bottomAxisHeight,
      priceLabelWidth: props.priceLabelWidth,
      minKWidth: props.minKWidth,
      maxKWidth: props.maxKWidth,
      panes: [{ id: 'main', ratio: 1 }], // 初始只有主图

      // 主/副图之间真实留白，形成视觉断开
      paneGap: 0,

      // 缩放级别配置
      zoomLevels: props.zoomLevels,
      initialZoomLevel: props.initialZoomLevel,
    },
  )

  // 注册主图渲染器插件
  chart.useRenderer(createGridLinesRendererPlugin()) // 网格线渲染到所有 pane
  chart.useRenderer(createExtremaMarkersRendererPlugin())
  chart.useRenderer(
    createMARendererPlugin({ ma5: true, ma10: true, ma20: true, ma30: true, ma60: true }),
  )
  chart.useRenderer(createBOLLRendererPlugin())
  chart.setRendererEnabled('boll', false) // 默认禁用，由语义化配置控制
  chart.useRenderer(createEXPMARendererPlugin())
  chart.setRendererEnabled('expma', false) // 默认禁用，由语义化配置控制
  chart.useRenderer(createENERendererPlugin())
  chart.setRendererEnabled('ene', false) // 默认禁用，由语义化配置控制
  chart.useRenderer(createCandleRenderer())
  chart.useRenderer(createLastPriceLineRendererPlugin())
  chart.useRenderer(createCustomMarkersRenderer()) // 自定义标记渲染器

  const axisWidth = props.rightAxisWidth + props.priceLabelWidth
  const getAxisCrosshair = () => {
    const pos = chart.interaction.crosshairPos
    const price = chart.interaction.crosshairPrice
    const activePaneId = chart.interaction.activePaneId
    if (pos && price !== null) {
      return { y: pos.y, price, activePaneId }
    }
    return null
  }

  // 系统渲染器插件
  chart.useRenderer(
    createYAxisRendererPlugin({
      axisWidth,
      yPaddingPx: props.yPaddingPx,
      getCrosshair: getAxisCrosshair,
    }),
  )
  // 主图指标图例（统一管理 MA、BOLL 等）
  chart.useRenderer(
    createMainIndicatorLegendRendererPlugin({
      yPaddingPx: props.yPaddingPx,
    }),
  )

  const subScaleRenderers = [
    { create: createVolumeScaleRendererPlugin, paneId: 'sub_VOLUME' },
    { create: createMacdScaleRendererPlugin, paneId: 'sub_MACD' },
    { create: createRsiScaleRendererPlugin, paneId: 'sub_RSI' },
    { create: createCciScaleRendererPlugin, paneId: 'sub_CCI' },
    { create: createStochScaleRendererPlugin, paneId: 'sub_STOCH' },
    { create: createMomScaleRendererPlugin, paneId: 'sub_MOM' },
    { create: createWmsrScaleRendererPlugin, paneId: 'sub_WMSR' },
    { create: createKstScaleRendererPlugin, paneId: 'sub_KST' },
    { create: createFastkScaleRendererPlugin, paneId: 'sub_FASTK' },
  ] as const

  for (const renderer of subScaleRenderers) {
    chart.useRenderer(
      renderer.create({
        axisWidth,
        paneId: renderer.paneId,
        yPaddingPx: props.yPaddingPx,
        getCrosshair: getAxisCrosshair,
      }),
    )
  }

  chart.useRenderer(
    createCrosshairRendererPlugin({
      getCrosshairState: () => ({
        pos: chart.interaction.crosshairPos,
        activePaneId: chart.interaction.activePaneId,
        isDragging: chart.interaction.isDraggingState(),
        price: chart.interaction.crosshairPrice,
      }),
    }),
  )
  chart.useRenderer(
    createTimeAxisRendererPlugin({
      height: props.bottomAxisHeight,
      getCrosshair: () => {
        const pos = chart.interaction.crosshairPos
        const idx = chart.interaction.crosshairIndex
        if (pos && idx !== null) {
          return { x: pos.x, index: idx }
        }
        return null
      },
    }),
  )

  chart.setOnViewportChange((vp) => {
    if (store.state.viewportDpr !== vp.dpr) {
      store.actions.setViewportDpr(vp.dpr)
    }
    if (store.state.viewWidth !== vp.plotWidth) {
      store.actions.setViewWidth(vp.plotWidth)
    }

    const newKGap = kGapFromDpr(vp.dpr)
    const zoomStateChanged = store.state.kGap !== newKGap
    if (zoomStateChanged) {
      store.actions.setZoomState(store.state.zoomLevel, store.state.kWidth, newKGap)
    }

    const chartState = chart.getOption()
    if (chartState.kWidth !== store.state.kWidth || chartState.kGap !== newKGap) {
      chart.applyRenderState(store.state.kWidth, newKGap, store.state.zoomLevel)
    }
  })
  chart.setOnPaneLayoutChange((panes) => {
    const next: Record<string, number> = {}
    for (const pane of panes) {
      next[pane.id] = pane.ratio
    }
    store.actions.setPaneRatios(next)

    const renderers = chart.getPaneRenderers()
    const borderTop = containerRef.value
      ? parseInt(getComputedStyle(containerRef.value).borderTopWidth) || 0
      : 0
    paneSeparatorLines.value = renderers.slice(0, -1).map((renderer) => {
      const pane = renderer.getPane()
      return {
        id: pane.id,
        top: pane.top + pane.height + borderTop,
      }
    })
  })

  chart.setOnDataChange((data) => {
    store.actions.setDataLength(data.length)
    store.actions.bumpDataVersion()
  })
  chartRef.value = chart

  // 同步初始 zoom 状态到 Chart（Chart 不持有业务 SSOT，由 store 驱动）
  chart.applyRenderState(store.state.kWidth, store.state.kGap, store.state.zoomLevel)

  // 初始化图表设置（从 LeftToolbar 获取默认设置）
  const initialSettings = toolbarRef.value?.getSettings() ?? { showVolumePriceMarkers: true }
  chart.updateSettings(initialSettings)

  // 初始化绘图交互控制器
  drawingController.value = new DrawingInteractionController(chart)
  drawingController.value.setCallbacks({
    onDrawingCreated: (drawing) => {
      store.actions.setDrawings([...store.state.drawings, drawing])
      store.actions.setSelectedDrawingId(drawing.id)
    },
    onToolChange: (toolId) => {
      // 可选：同步工具状态到外部
    },
    onDrawingSelected: (drawing) => {
      store.actions.setSelectedDrawingId(drawing?.id ?? null)
    },
  })

  chart.interaction.setTooltipAnchorPositioning(useAnchorPositioning.value)
  chart.interaction.setOnInteractionChange((snapshot) => {
    interactionState.value = snapshot
  })

  // 设置捏合缩放回调（移动端双指缩放）
  chart.interaction.setOnPinchZoom((delta, centerClientX) => {
    const container = containerRef.value
    if (!container || !chart) return
    const rect = container.getBoundingClientRect()
    const centerX = centerClientX - rect.left
    const scrollLeft = container.scrollLeft
    const dpr = chart.getCurrentDpr()

    const result = computeZoom(
      delta,
      centerX,
      scrollLeft,
      zoomLevel.value,
      kWidth.value,
      kGap.value,
      {
        minKWidth: props.minKWidth,
        maxKWidth: props.maxKWidth,
        zoomLevelCount: props.zoomLevels,
        dpr,
      },
    )
    if (!result) return

    // 更新 Vue 响应式状态 → totalWidth 重算
    store.actions.setZoomState(result.targetLevel, result.newKWidth, result.newKGap)

    // 清除 hover + crosshair
    chart.interaction.clearHover()

    nextTick(() => {
      const c = containerRef.value
      if (!c) return
      const maxScrollLeft = Math.max(0, c.scrollWidth - c.clientWidth)
      const clampedScrollLeft = Math.min(Math.max(0, result.newScrollLeft), maxScrollLeft)
      c.scrollLeft = Math.round(clampedScrollLeft * dpr) / dpr
      chart.applyRenderState(result.newKWidth, result.newKGap, result.targetLevel)
      emit('zoomLevelChange', result.targetLevel, result.newKWidth)
    })
  })
  interactionState.value = chart.interaction.getInteractionSnapshot()
  store.actions.setViewportDpr(chart.getCurrentDpr())
  chart.resize()

  // 初始化语义化控制器
  semanticController.value = new SemanticChartController(chart)
  semanticController.value.on('config:error', (error) => {
    console.error('Semantic config error:', error)
  })
  semanticController.value.on('config:ready', () => {
    store.actions.setDataLength(chart.getData()?.length ?? 0)
    store.actions.bumpDataVersion()

    // 从语义化配置初始化指标状态（单向数据流：config → state → chart）
    initIndicatorsFromConfig()

    // 同步副图状态（副图由 Chart API 动态创建）
    syncSubPanesFromChart()

    nextTick(() => scrollToRight())
  })

  // 应用语义化配置（必需，会创建副图）
  semanticController.value.applyConfig(props.semanticConfig).then((result) => {
    if (result && !result.success) {
      console.error('Semantic config apply failed:', result.errors)
    }
  })

  // 保存 wheel handler，确保 onUnmounted 能正确移除
  ;(chart as any).__onWheel = onWheelHandler
})

onUnmounted(() => {
  const chart = chartRef.value
  if (chart) {
    const onWheel = (chart as any).__onWheel as
      | ((this: HTMLElement, ev: WheelEvent) => any)
      | undefined
    const container = containerRef.value
    if (onWheel && container) container.removeEventListener('wheel', onWheel)
    chart.destroy()
  }
  chartRef.value = null
  drawingController.value = null
})

// kWidth/kGap 由 zoomLevel 派生，不再通过 props 直接修改
// 如需程序化控制缩放，请使用 expose 的 zoomToLevel/zoomIn/zoomOut 方法

// 监听 yPaddingPx 变化
watch(
  () => props.yPaddingPx,
  (newVal) => {
    chartRef.value?.updateOptions({ yPaddingPx: newVal })
  },
)

// 监听 semanticConfig 变化（唯一数据源）
watch(
  () => props.semanticConfig,
  async (newConfig, oldConfig) => {
    if (newConfig && newConfig !== oldConfig) {
      const result = await semanticController.value?.applyConfig(newConfig)
      if (result && !result.success) {
        console.error('Semantic config apply failed:', result.errors)
      }
    }
  },
  { deep: true },
)
</script>

<style scoped>
.chart-wrapper {
  /* CSS 变量支持自定义尺寸 */
  --kmap-height: var(--kmap-chart-height, 100%);
  --kmap-width: var(--kmap-chart-width, 100%);

  /* 让组件在父容器中居中显示 */
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--kmap-width);
  height: var(--kmap-height);
  min-height: 300px; /* 默认最小高度，确保容器有有效尺寸 */
  flex-direction: column;
}

.chart-stage {
  width: 95%;
  height: 85%;
  min-height: 255px;
  display: flex;
  align-items: stretch;
  gap: 8px;
}

.chart-main {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: stretch;
  gap: 0;
  position: relative;
}

.pane-separator-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 20;
}

.pane-separator-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 0;
  border-top: 1px solid #e5e7eb;
  opacity: 1;
  box-sizing: border-box;
  transition:
    border-top-color 120ms ease,
    border-top-width 120ms ease,
    margin-top 120ms ease,
    opacity 120ms ease;
}

.pane-separator-line.is-active {
  border-top-color: #3b82f6;
  border-top-width: 2px;
  margin-top: -1px;
}

.chart-stage.is-resizing-pane,
.chart-stage.is-hovering-pane-separator {
  cursor: ns-resize;
}

.chart-stage.is-hovering-kline {
  cursor: pointer;
}

.chart-stage.is-hovering-right-axis {
  cursor: ns-resize;
}

.chart-stage.is-dragging {
  cursor: grabbing;
}

.chart-container {
  position: relative;
  flex: 1 1 auto;
  overflow-x: auto;
  overflow-y: hidden;
  height: 100%;
  min-height: inherit;
  scrollbar-width: none;
  -ms-overflow-style: none;
  border: 1px solid #e5e7eb;
  border-right: 0;
  border-radius: 6px 0 0 6px;
  box-sizing: border-box;
  background: #ffffff;

  /* ===== 移动端：屏蔽长按弹出菜单/选择等默认行为，避免影响交互 ===== */
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  /* 禁止浏览器接管手势（如长按/双击缩放等），保留我们自定义的 pointer 拖拽/十字线逻辑 */
  touch-action: none;
}

.chart-container::-webkit-scrollbar {
  display: none;
}

.chart-container:hover {
  cursor: crosshair;
}

.chart-stage.is-resizing-pane .chart-container,
.chart-stage.is-hovering-pane-separator .chart-container {
  cursor: ns-resize;
}

.chart-stage.is-hovering-kline .chart-container {
  cursor: pointer;
}

.chart-stage.is-dragging .chart-container {
  cursor: grabbing;
}

.right-axis-host {
  position: relative;
  flex: 0 0 auto;
  height: 100%;
  min-height: inherit;
  box-sizing: border-box;
  background: #ffffff;
  overflow: visible;
  border: 1px solid #e5e7eb;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;

  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  touch-action: none;
}


.scroll-content {
  height: 100%;
  min-height: inherit;
  position: relative;
}

/* 关键：sticky 固定在可视区域左上角 */
.canvas-layer {
  position: sticky;
  left: 0;
  top: 0;
  /* width/height 由 JS 在 render() 中设置为视口大小 */
  pointer-events: none;
}

.tooltip-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 30;
}

.tooltip-anchor {
  position: absolute;
  width: 1px;
  height: 1px;
  pointer-events: none;
}

.tooltip-anchor.kline-tooltip-anchor.use-anchor {
  anchor-name: --kline-tooltip-anchor;
}

.tooltip-anchor.marker-tooltip-anchor.use-anchor {
  anchor-name: --marker-tooltip-anchor;
}

@media (max-width: 768px), (max-height: 640px) {
  .chart-stage {
    gap: 6px;
  }
}
</style>

<!-- 非 scoped 样式：用于动态创建的 canvas 元素 -->
<style>
/* plot canvas 基础样式 */
.plot-canvas {
  position: absolute;
  left: 0;
  top: 0;
  display: block;
}

/* 右侧价格轴 */
.right-axis {
  position: absolute;
  display: block;
  left: 0;
}

/* 底部时间轴 */
.x-axis-canvas {
  position: absolute;
  left: 0;
  bottom: 0;
  display: block;
  z-index: 10;
}

.right-axis {
  z-index: 15;
}
</style>
